import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { i18n } from './i18n';
import { useOrgId } from './org';

/**
 * Chat with your accountant (document 06 C5 · Messages): one conversation per client between its
 * people and Outss staff. Types mirror apps/api/src/messages/dto.ts.
 */
export interface MessageView {
  id: string;
  body: string;
  createdAt: string;
  author: { id: string; name: string; side: 'client' | 'accountant' };
  /** Written by the signed-in user. */
  mine: boolean;
}

export interface MessageList {
  /** Oldest first. */
  messages: MessageView[];
  /** More messages exist before the first one. */
  hasMore: boolean;
  /** Messages from others the caller has not read. */
  unread: number;
}

/** The API refuses longer messages. */
export const MESSAGE_MAX = 4000;

/** Messages page size: the latest page is polled, older pages are loaded on request. */
export const MESSAGE_PAGE = 50;

/** While the Messages page is open. */
export const MESSAGE_POLL_MS = 15_000;

export function messagesPath(orgId: string, opts: { before?: string; limit?: number } = {}) {
  const q = new URLSearchParams({ limit: String(opts.limit ?? MESSAGE_PAGE) });
  // URLSearchParams encodes a "+hh:mm" offset, which a raw query string would turn into a space
  if (opts.before) q.set('before', opts.before);
  return `/orgs/${orgId}/messages?${q}`;
}

/**
 * One list, oldest first, from pages that may overlap (the polled latest page and older pages
 * loaded with `before`): each message once, ordered by time then id.
 */
export function mergeMessages(...lists: MessageView[][]): MessageView[] {
  const byId = new Map<string, MessageView>();
  for (const list of lists) for (const m of list) byId.set(m.id, m);
  return [...byId.values()].sort(
    (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt) || a.id.localeCompare(b.id),
  );
}

/** Which side wrote it: "Outss" for the accountant side, else the client's name. */
export function sideLabel(m: Pick<MessageView, 'author'>, clientName?: string | null): string {
  return m.author.side === 'accountant' ? 'Outss' : clientName?.trim() || i18n.t('messages.client');
}

/** Why the composer cannot send `text` yet, or null when it can. */
export function draftProblem(text: string): string | null {
  if (!text.trim()) return i18n.t('messages.placeholder');
  if (text.length > MESSAGE_MAX) return i18n.t('messages.tooLong');
  return null;
}

/** Ctrl+Enter, or Cmd+Enter on macOS, sends from the composer. */
export const isSendKey = (e: { key: string; ctrlKey: boolean; metaKey: boolean }): boolean =>
  e.key === 'Enter' && (e.ctrlKey || e.metaKey);

/** The latest page, polled while the page is open. */
export function useLatestMessages() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['messages', orgId, 'latest'],
    queryFn: () => api<MessageList>(messagesPath(orgId!)),
    enabled: Boolean(orgId),
    refetchInterval: MESSAGE_POLL_MS,
  });
}

/** Unread count for the navigation badge: one message per request, polled like notifications. */
export function useMessagesUnread(enabled = true) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['messages', orgId, 'unread'],
    queryFn: () => api<MessageList>(messagesPath(orgId!, { limit: 1 })).then((r) => r.unread),
    enabled: Boolean(orgId) && enabled,
    refetchInterval: 60_000,
  });
}

export function useSendMessage() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api<MessageView>(`/orgs/${orgId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages', orgId, 'latest'] }),
  });
}

/** Moves the caller's read marker to now. */
export function useMarkMessagesRead() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ unread: number }>(`/orgs/${orgId}/messages/read`, { method: 'POST' }),
    onSuccess: () => {
      qc.setQueryData<MessageList>(['messages', orgId, 'latest'], (d) =>
        d ? { ...d, unread: 0 } : d,
      );
      qc.setQueryData(['messages', orgId, 'unread'], 0);
    },
  });
}
